import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getSessionApi, getGuestStore } from "../../../utils/demoApi";
import MeetingProviderRouter from "../../Classroom/MeetingProviderRouter";
import { AppButton } from "../../ui";
import { resolveSessionKind } from "../sessionKind";
import useAttendeeToken from "./useAttendeeToken";
import "../demo-classes.scss";

const HEARTBEAT_INTERVAL_MS = 30000;

const DemoLivePage = ({ kind = "demo" }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const vocab = resolveSessionKind(kind);
  const api = getSessionApi(vocab.kind);
  const guestStore = getGuestStore(vocab.kind);
  // Absorbs `?t=`, so a "we're live now" mail opened on a new device drops
  // straight into the room instead of the registration form.
  const attendeeToken = useAttendeeToken(vocab.kind, slug);
  const [zoomConfig, setZoomConfig] = useState(null);
  const [error, setError] = useState("");
  const heartbeatRef = useRef(null);

  // Read guest token & fetch SDK signature
  useEffect(() => {
    if (!attendeeToken) {
      navigate(`${vocab.publicBase}/${slug}/register`, { replace: true });
      return undefined;
    }

    let cancelled = false;
    api.getJoinConfig(slug, attendeeToken)
      .then(({ data }) => {
        if (!cancelled) setZoomConfig(data);
      })
      .catch((err) => {
        if (cancelled) return;
        // The BE answers `errorCode`; `error` was the older shape. Read both so
        // a NOT_LIVE never falls through to the generic message.
        const code = err?.response?.data?.errorCode || err?.response?.data?.error;
        if (code === "NOT_LIVE") {
          navigate(`${vocab.publicBase}/${slug}/lobby`, { replace: true });
          return;
        }
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          guestStore.clear(slug);
          navigate(`${vocab.publicBase}/${slug}/register`, { replace: true });
          return;
        }
        setError(err?.response?.data?.message || "Could not connect to the live session.");
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, navigate, kind, attendeeToken]);

  // Heartbeat while in the meeting
  useEffect(() => {
    if (!zoomConfig || !attendeeToken) return undefined;

    // Each beat also says whether the room is still live. A room ended from the
    // host's side (or closed by the platform) does not always take the embed
    // with it; this is what moves the attendee on to their certificate and the
    // feedback form. A full navigation, as on meeting end, so the SDK is torn down.
    const tick = () => api.heartbeat(slug, attendeeToken)
      .then(({ data }) => {
        if (data?.status === "ended" || data?.status === "cancelled") {
          window.location.assign(`${vocab.publicBase}/${slug}/thank-you`);
        }
      })
      .catch(() => {});
    tick();
    heartbeatRef.current = setInterval(tick, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(heartbeatRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomConfig, slug, kind, attendeeToken]);

  if (error) {
    return (
      <div className="demo-live">
        <div className="demo-live__loading">
          <p className="demo-public__error">{error}</p>
          <AppButton variant="default" onClick={() => navigate(`${vocab.publicBase}/${slug}/lobby`)}>
            Back to lobby
          </AppButton>
        </div>
      </div>
    );
  }

  if (!zoomConfig) {
    return (
      <div className="demo-live">
        <div className="demo-live__loading">
          <span className="demo-public__pulse">Connecting to the live room…</span>
        </div>
      </div>
    );
  }

  // The provider is whatever the institute runs its live classes on — the
  // router picks the embed, so neither product needs to know which one.
  return (
    <MeetingProviderRouter
      joinConfig={zoomConfig}
      onMeetingEnd={() => { window.location.href = `${vocab.publicBase}/${slug}/thank-you`; }}
      // A dropped connection rejoins in place rather than sending the attendee
      // to the thank-you page halfway through.
      onRejoin={async () => (await api.getJoinConfig(slug, attendeeToken)).data}
      leaveUrl={`${window.location.origin}${vocab.publicBase}/${slug}/thank-you`}
    />
  );
};

export default DemoLivePage;
